<script setup lang="ts">
import { ref, computed, inject } from "vue";
import { CompareService } from "../services/compare.service";
import { SITEURL } from "@libTs/cms.globals";
import { itemCodeToUrlSlug } from "../helpers/url.helper";
import { vClickOutside } from "../directives/v-click-outside";

const compareSvc = inject<CompareService>("compareSvc") as CompareService;

const groups = computed(() => {
    const grouped: { [key: string]: any[] } = {};
    compareSvc.list.forEach((item) => {
        const group = item.Group || "Ungrouped";
        if (!grouped[group]) {
            grouped[group] = [];
        }
        grouped[group].push(item);
    });
    return grouped;
});

const openGroup = ref<string | null>(null);
</script>

<template>
    <div
        class="relative border-b-4 h-full flex items-center w-6 justify-center border-t-transparent border-t-4"
        :class="{
            'border-b-primary': compareSvc.openComparelist,
            'border-b-transparent': !compareSvc.openComparelist,
        }"
        v-click-outside="() => (compareSvc.openComparelist = false)"
    >
        <button
            class="btn-compare"
            @click="compareSvc.openComparelist = !compareSvc.openComparelist"
            title="Comparison list"
        ></button>
        <div
            v-if="compareSvc.openComparelist"
            class="absolute top-20 right-0 w-auto min-w-[400px] bg-white shadow-lg z-50 pt-8 pb-8 px-6"
        >
            <button
                @click="compareSvc.openComparelist = false"
                class="absolute top-6 right-6 text-grey-500 hover:text-grey-700"
            >
                <i class="fal fa-times text-f3"></i>
            </button>
            <div
                class="pb-4 border-b border-grey-200 flex justify-between items-center"
            >
                <h3 class="text-f4 font-bold">Comparison lists</h3>
            </div>
            <div v-if="compareSvc.list.length === 0" class="py-4">
                Your comparison lists are empty.
            </div>
            <div v-else class="">
                <div class="" v-for="(group, index) in groups" :key="index">
                    <div
                        class="flex justify-between items-center cursor-pointer py-5"
                    >
                        <div class="flex items-center gap-3">
                            <span
                                @click.stop="
                                    openGroup =
                                        openGroup === index ? null : index
                                "
                            >
                                <svg
                                    v-if="openGroup === index"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-6 w-6 transform rotate-180 transition-transform duration-300"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="#009aa7"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                                <svg
                                    v-else
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="h-6 w-6 transform transition-transform duration-300"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="#009aa7"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </span>
                            <h4
                                class="text-f5 font-bold"
                                @click.stop="
                                    openGroup =
                                        openGroup === index ? null : index
                                "
                            >
                                {{ index }}
                            </h4>
                            <span
                                class="text-f5 font-bold bg-primary text-white rounded-full flex items-center justify-center w-6 h-6"
                                @click.stop="
                                    openGroup =
                                        openGroup === index ? null : index
                                "
                            >
                                {{ group.length }}
                            </span>
                        </div>
                        <div class="flex items-center gap-6">
                            <a
                                v-if="group.length > 1"
                                :href="'/shop/compare/' + index"
                                class="btn-cb px-3 py-1 rounded-s"
                                >compare</a
                            >
                            <div
                                class="btn-light-grey px-3 py-1 rounded-s cursor-not-allowed"
                                v-else
                            >
                                compare
                            </div>
                            <button
                                class="text-f4"
                                @click.stop="compareSvc.removeGroup(index)"
                            >
                                <i class="far fa-trash"></i>
                            </button>
                        </div>
                    </div>
                    <ul class="" v-if="openGroup === index">
                        <li
                            v-for="(item, index) in group"
                            :key="index"
                            class="border-t border-grey-200 border-dotted py-4 flex items-start last:border-b-0"
                        >
                            <div class="flex items-start w-full gap-5">
                                <img
                                    v-if="item.ImageURL"
                                    :src="item.ImageURL"
                                    alt="Item Image"
                                    class="w-20 h-11 object-contain mr-4 rounded-sm shrink-0 grow-0"
                                />
                                <div
                                    class="bg-grey-30 rounded-sm w-20 h-11 mr-4 flex items-center justify-center shrink-0 grow-0"
                                    v-else
                                >
                                    <img
                                        :src="
                                            SITEURL +
                                            'assets/icons/no-photo.svg'
                                        "
                                        alt="No Image Available"
                                        class="w-4 h-4 object-contain"
                                    />
                                </div>
                                <a
                                    :href="'/product/' + itemCodeToUrlSlug(item.ItemCode)"
                                    class="flex-grow"
                                >
                                    <h4
                                        class="text-f6 font-bold break-word hyphens-auto"
                                    >
                                        {{ item.ItemName }}
                                    </h4>
                                </a>
                                <button
                                    class=""
                                    @click.stop="compareSvc.remove(item)"
                                >
                                    <i class="far fa-trash"></i>
                                </button>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
